import React, { useRef } from "react";
import {
  Box,
  Text,
  Image,
  Link,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FiArrowUpRight, FiX } from "react-icons/fi";
import {
  accent,
  surface,
  border as borderColor,
} from "../../constants/constants";

interface CompanyCardProps {
  companyName: string;
  logoPlaceholder: string;
  descriptionPlaceholder: string;
  weblink: string;
  datesWorked: string;
  position: string;
  colorGradient: string;
  fontFamily: string;
  fontWeight: number;
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  companyName,
  logoPlaceholder,
  descriptionPlaceholder,
  weblink,
  datesWorked,
  position,
  colorGradient,
  fontFamily,
  fontWeight,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Explicit return-focus target: some browsers (notably Safari) don't focus
  // a <button> on click, so Chakra's default "restore to whatever was
  // document.activeElement" can miss and fall back to the first focusable
  // element on the page (the skip link), which yanks the scroll position to
  // the very top when the modal closes. Pointing finalFocusRef at the actual
  // trigger makes the restore deterministic regardless of that quirk.
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Full-screen sheet on mobile gets a slide-down close; desktop dialog keeps the scale fade.
  const modalMotionPreset = useBreakpointValue<"slideInBottom" | "scale">({
    base: "slideInBottom",
    md: "scale",
  });

  return (
    <>
      <Box
        as="button"
        ref={triggerRef}
        role="group"
        onClick={onOpen}
        bg={surface}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="16px"
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={9}
        px={6}
        transition="border-color 0.2s ease, transform 0.2s ease"
        _hover={{ borderColor: accent, transform: "translateY(-4px)" }}
      >
        <HStack spacing={4}>
          {/* Decorative: the visible company name text right after it already
              gives this an accessible name, so the logo would only add noise. */}
          <Image
            src={logoPlaceholder}
            alt=""
            boxSize={{ base: "44px", md: "56px" }}
            objectFit="contain"
            borderRadius="full"
            flexShrink={0}
            filter="grayscale(1)"
            transition="filter 0.3s ease"
            _groupHover={{ filter: "grayscale(0)" }}
          />
          <Text
            fontSize={{ base: "2xl", md: "3xl" }}
            fontFamily={fontFamily}
            fontWeight={fontWeight}
            color="brand.text"
          >
            {companyName}
          </Text>
        </HStack>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size={{ base: "full", md: "md" }}
        motionPreset={modalMotionPreset}
        finalFocusRef={triggerRef}
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent
          aria-label={companyName}
          bg={surface}
          borderRadius={{ base: 0, md: "2xl" }}
          overflow="hidden"
        >
          <Box bgGradient={colorGradient} height="90px" position="relative">
            <ModalCloseButton color="white" _hover={{ bg: "whiteAlpha.300" }} />
          </Box>
          <ModalBody pb={8} pt={0}>
            <Flex
              direction="column"
              alignItems="center"
              textAlign="center"
              mt="-40px"
              position="relative"
            >
              <Image
                src={logoPlaceholder}
                alt=""
                boxSize="80px"
                objectFit="contain"
                borderRadius="full"
                border="4px solid"
                borderColor={surface}
                bg={surface}
                mb={4}
              />
              <Text
                fontSize="2xl"
                fontFamily={fontFamily}
                fontWeight={fontWeight}
                color="brand.text"
              >
                {companyName}
              </Text>
              <Text fontSize="md" color="brand.muted" mb={4}>
                {position} • {datesWorked}
              </Text>
              <Text fontSize="md" color="brand.muted" lineHeight="1.7" mb={6}>
                {descriptionPlaceholder}
              </Text>
              <Link
                href={weblink}
                color={accent}
                isExternal
                fontWeight={600}
                display="inline-flex"
                alignItems="center"
                gap={2}
              >
                <Icon as={FiArrowUpRight} aria-hidden="true" boxSize={5} />
                Learn more about {companyName}
              </Link>

              {/* Thumb-reachable close control for mobile's full-screen sheet */}
              <Box
                as="button"
                type="button"
                onClick={onClose}
                aria-label={`Close ${companyName} details`}
                display={{ base: "flex", md: "none" }}
                alignItems="center"
                justifyContent="center"
                boxSize="44px"
                borderRadius="full"
                border="1px solid"
                borderColor={borderColor}
                color="brand.muted"
                mt={8}
                transition="transform 0.2s ease, border-color 0.2s ease, color 0.2s ease"
                _hover={{ borderColor: accent, color: accent }}
                _active={{ transform: "scale(0.9)" }}
              >
                <Icon as={FiX} aria-hidden="true" boxSize={5} />
              </Box>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CompanyCard;
